import { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import {
  getstaffMembers,
  getstaffMembersScroll,
  getOfficesDetail,
} from "actions/index";
import {
  exportStaff,
  getGlobalStaff,
  downloadTemplateXL,
} from "repositories/staff-repository";
import { decodeId, downloadFile, b64toBlob } from "utils";
import toast from "react-hot-toast";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import constants from "../../constants";
export const useStaffDataFetchingMethods = ({ t }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const {
    selectedId,
    isStaff,
    isOwner,
    dataAccessValues,
  } = useAccountOwnerCommon();
  
  const history = useHistory();
  const officeId = decodeId(params.id);
  const globalStaffPage = location.pathname.includes("global-staff");
  const canAccessThisPage = isOwner || (isStaff && (globalStaffPage?dataAccessValues?.isAdmin:true));
  const isAdmin = globalStaffPage
    ? canAccessThisPage
    : !!location.state?.isAdmin;

  // Redux selectors
  const pagnation = useSelector((state) => state.staff.pagnation);
  const officeDetail = useSelector((state) => state.offices.officeDetail);

  // States for global staff
  const [globalStaffMembers, setGlobalStaffMembers] = useState({
    data: [],
    totalPages: 0,
  });
  const [globalStaffMembersLoading, setGlobalStaffMembersLoading] =
    useState(false);
  const [globalStaffPagination, setGlobalStaffPagination] = useState(1);

  // States for search and filters
  const [searchType, setSearchType] = useState("");
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Refs
  const debounceSearchRef = useRef(null);
  useEffect(() => {
    if (searchType.length && selectedId) {
      setSearchType("");
    }
  }, [selectedId]);
  // Initialize staff data
  const initializeStaffData = useCallback(
    async (searchTerm = "", allFilters = {}) => {
      if(!canAccessThisPage) return;
      const shouldIncludeAdmins = isStaff && isAdmin;
      const payload = {
        searchTerm: searchTerm,
        pageSize: 10,
        IncludeAdmins: shouldIncludeAdmins,
        ...allFilters,
      };
      if (payload.sortId) {
        delete payload.sortId;
      }

      if (globalStaffPage) {
        if (!selectedId) return;
        setGlobalStaffMembersLoading(true);
        try {
          payload.ownerId = selectedId;
          payload.pageNumber = 1;
          const res = await getGlobalStaff(payload);
          if (res.data.length) {
            setGlobalStaffMembers({
              data: [...res.data],
              totalPages: res.pagination.totalPages,
            });
          } else {
            setGlobalStaffMembers({
              data: [],
              totalPages: 0,
            });
          }
          setGlobalStaffPagination(1);
        } catch (error) {
          toast.error(error?.message);
          setGlobalStaffMembers({
            data: [],
            totalPages: 0,
          });
        } finally {
          setGlobalStaffMembersLoading(false);
        }
      } else {
        payload.officeId = parseInt(officeId);
        payload.pageNo = 1;
        dispatch(getstaffMembers(payload));
      }
    },
    [globalStaffPage, selectedId, officeId, isStaff, isAdmin, dispatch, canAccessThisPage]
  );

  // Fetch more data for pagination
  const fetchMoreData = useCallback(
    async (allFilters = {}) => {
      if(!canAccessThisPage) return;
      const payload = {
        searchTerm: searchType,
        pageSize: 10,
        IncludeAdmins: includeAdmins,
        ...allFilters,
      };
      if (payload.sortId) {
        delete payload.sortId;
      }

      if (globalStaffPage) {
        if (
          globalStaffMembersLoading ||
          globalStaffMembers.totalPages <= globalStaffPagination
        ) {
          return;
        }
        setGlobalStaffMembersLoading(true);
        try {
          payload.ownerId = selectedId;
          payload.pageNumber = globalStaffPagination + 1;
          const res = await getGlobalStaff(payload);
          if (res.data.length) {
            setGlobalStaffMembers((prev) => ({
              data: [...prev.data, ...res.data],
              totalPages: res.pagination.totalPages,
            }));
          }
          setGlobalStaffPagination((prev) => prev + 1);
        } catch (error) {
          toast.error(error?.message);
        } finally {
          setGlobalStaffMembersLoading(false);
        }
      } else {
        payload.officeId = parseInt(officeId);
        payload.pageNo = pagnation?.currentPage + 1;
        if (pagnation && pagnation.currentPage !== pagnation.totalPages) {
          dispatch(getstaffMembersScroll({ ...payload }));
        }
      }
    },
    [
      globalStaffPage,
      selectedId,
      officeId,
      searchType,
      includeAdmins,
      globalStaffMembersLoading,
      globalStaffMembers.totalPages,
      globalStaffPagination,
      pagnation,
      dispatch,
      canAccessThisPage
    ]
  );

  // Export staff data
  const downloadAllExportFile = useCallback(
    async (type, allFilters = {}, searchTerm = "") => {
      if (
        (!globalStaffPage && !officeDetail?.ownerId) ||
        (globalStaffPage && !selectedId)
      )
        return;
      setExportLoading(true);
      try {
        // Check if this is a template download request
        if (type === "template") {
          const ownerId = globalStaffPage ? selectedId : officeDetail?.ownerId;
          const resp = await downloadTemplateXL(
            ownerId,
            globalStaffPage,
            officeId
          );
          // The API returns base64 string in file_Url field or directly in response
          const base64Data =
            resp?.file_Url ||
            resp?.data?.file_Url ||
            (typeof resp === "string" ? resp : null);

          if (base64Data) {
            // Check if it's a URL (http/https) or data URI, otherwise treat as base64
            const isUrl =
              base64Data.startsWith("http://") ||
              base64Data.startsWith("https://") ||
              base64Data.startsWith("data:");

            if (isUrl) {
              downloadFile(base64Data);
            } else {
              // Convert base64 string to blob and download as .xlsx
              const blob = b64toBlob(
                base64Data,
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              );
              const blobUrl = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = blobUrl;
              link.download = "staff_template.xlsx";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(blobUrl);
            }
          }
        } else {
          const shouldIncludeAdmins = isStaff && isAdmin;
          const payload = {
            ...(type ? { type } : allFilters),
            officeIds: globalStaffPage
              ? type
                ? []
                : allFilters.officeIds
              : [parseInt(officeId)],
            searchTerm: type ? "" : searchTerm,
            IncludeAdmins: shouldIncludeAdmins,
          };
          const resp = await exportStaff({
            ...payload,
            Ownerid: globalStaffPage ? selectedId : officeDetail?.ownerId,
          });
          if (resp?.data?.file_Url) {
            downloadFile(resp?.data?.file_Url);
          }
        }
      } catch (error) {
        toast.error(error?.message);
      } finally {
        setExportLoading(false);
      }
    },
    [
      globalStaffPage,
      selectedId,
      officeId,
      officeDetail?.ownerId,
      isStaff,
      isAdmin,
    ]
  );

  // Handle search with debounce
  const handleSearchFilter = useCallback(
    (e, allFilters = {}) => {
      const searchValue = e.target.value;
      setSearchType(searchValue);
      clearTimeout(debounceSearchRef.current);
      debounceSearchRef.current = setTimeout(() => {
        initializeStaffData(searchValue, allFilters);
      }, 500);
    },
    [initializeStaffData, canAccessThisPage]
  );
  useEffect(() => {
    if (
      !globalStaffPage &&
      isStaff &&
      officeId &&
      officeDetail?.id === +officeId &&
      !officeDetail?.isActive
    ) {
      toast.error(officeDetail?.name + " " + t("accountOwner.isDeactivated"));
      history.push(constants.routes.staff.offices);
    }
  }, [isStaff, officeDetail?.isActive]);
  // Initialize office detail for non-global staff page
  useEffect(() => {
    if (globalStaffPage) return;
    const shouldIncludeAdmins = isStaff && isAdmin;
    setIncludeAdmins(shouldIncludeAdmins);
    dispatch(getOfficesDetail({ Id: officeId }));
  }, [isAdmin, officeId]);

  return {
    // States
    globalStaffMembers,
    globalStaffMembersLoading,
    globalStaffPagination,
    searchType,
    setSearchType,
    includeAdmins,
    exportLoading,

    // Methods
    initializeStaffData,
    fetchMoreData,
    downloadAllExportFile,
    handleSearchFilter,
    setGlobalStaffMembers,
    dispatch,
    // Refs
    debounceSearchRef,
  };
};
